/**
 * Subscription Selector Component
 * Handles subscription/one-time purchase toggle on product pages
 * and subscription changes in the cart
 */

if (!customElements.get('subscription-selector')) {
  class SubscriptionSelector extends HTMLElement {
    constructor() {
      super();
      this.sellingPlanInput = this.querySelector('.subscription-selector__input');
      this.radioButtons = this.querySelectorAll('.subscription-selector__radio');
      this.frequencySelect = this.querySelector('[data-selling-plan-select]');
      this.frequencyContainer = this.querySelector('[data-frequency-selector]');
      this.detailsButton = this.querySelector('[data-subscription-details]');
      this.sellingPlansData = this.getSellingPlansData();
    }

    connectedCallback() {
      this.radioButtons.forEach(radio => {
        radio.addEventListener('change', this.onPurchaseOptionChange.bind(this));
      });

      if (this.frequencySelect) {
        this.frequencySelect.addEventListener('change', this.onFrequencyChange.bind(this));
      }

      if (this.detailsButton) {
        this.detailsButton.addEventListener('click', this.showDetailsModal.bind(this));
      }

      // Initialize with default selection
      this.initializeSelection();

      // Listen for variant changes
      document.addEventListener('variant:change', this.onVariantChange.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariantChange.bind(this));
    }

    getSellingPlansData() {
      const jsonScript = this.querySelector('[data-selling-plans-json]');
      if (jsonScript) {
        try {
          return JSON.parse(jsonScript.textContent);
        } catch (e) {
          console.error('Error parsing selling plans data:', e);
          return null;
        }
      }
      return null;
    }

    initializeSelection() {
      const checkedRadio = this.querySelector('.subscription-selector__radio:checked');
      if (checkedRadio) {
        this.updateSellingPlan(checkedRadio.dataset.sellingPlanId);
        this.updateButtonText(checkedRadio.value);
      }
    }

    onPurchaseOptionChange(event) {
      const selectedOption = event.target.value;
      let sellingPlanId = '';

      if (selectedOption === 'subscribe') {
        // Get selling plan from frequency selector or default
        if (this.frequencySelect) {
          sellingPlanId = this.frequencySelect.value;
        } else {
          sellingPlanId = event.target.dataset.sellingPlanId;
        }
        
        // Show frequency selector
        if (this.frequencyContainer) {
          this.frequencyContainer.hidden = false;
        }
      } else {
        // One-time purchase - no selling plan
        sellingPlanId = '';
        
        // Hide frequency selector
        if (this.frequencyContainer) {
          this.frequencyContainer.hidden = true;
        }
      }

      this.updateSellingPlan(sellingPlanId);
      this.updateButtonText(selectedOption);
      this.updatePriceDisplay(selectedOption, sellingPlanId);
    }

    onFrequencyChange(event) {
      const sellingPlanId = event.target.value;
      this.updateSellingPlan(sellingPlanId);
      this.updatePriceDisplay('subscribe', sellingPlanId);
    }

    updateSellingPlan(sellingPlanId) {
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = sellingPlanId || '';
      }

      // Dispatch event for other components
      this.dispatchEvent(new CustomEvent('selling-plan:change', {
        bubbles: true,
        detail: { sellingPlanId }
      }));
    }

    updateButtonText(purchaseType) {
      const section = this.closest('product-info, [data-section-id]');
      const productForm = section?.querySelector('.product-form__submit');
      if (!productForm) return;

      const buttonText = productForm.querySelector('[data-button-text]');
      if (!buttonText) return;

      const subscribeText = buttonText.dataset.subscribeText || 'Set Up Replenish';
      const originalText = buttonText.dataset.originalText || 'Add to cart';

      if (purchaseType === 'subscribe') {
        buttonText.textContent = subscribeText;
      } else {
        buttonText.textContent = originalText;
      }
    }

    updatePriceDisplay(purchaseType, sellingPlanId) {
      if (!this.sellingPlansData) return;

      const priceElement = this.closest('product-info')?.querySelector('.price-item--regular');
      if (!priceElement) return;

      const variantPrice = this.sellingPlansData.variant_price;
      let displayPrice = variantPrice;

      if (purchaseType === 'subscribe' && sellingPlanId) {
        const sellingPlan = this.sellingPlansData.selling_plans.find(
          sp => sp.id.toString() === sellingPlanId.toString()
        );

        if (sellingPlan) {
          if (sellingPlan.price_adjustment_type === 'percentage') {
            const discount = variantPrice * (sellingPlan.price_adjustment_value / 100);
            displayPrice = variantPrice - discount;
          } else if (sellingPlan.price_adjustment_type === 'fixed_amount') {
            displayPrice = variantPrice - sellingPlan.price_adjustment_value;
          }
        }
      }

      // Update the subscription option price display
      const subscribeOption = this.querySelector('[data-option="subscribe"] .subscription-selector__price');
      if (subscribeOption && purchaseType === 'subscribe') {
        subscribeOption.textContent = this.formatMoney(displayPrice);
      }
    }

    formatMoney(cents) {
      // Simple money formatter - adjust based on your store's currency settings
      const amount = (cents / 100).toFixed(2);
      return Shopify.currency.active 
        ? `${Shopify.currency.active}${amount}` 
        : `$${amount}`;
    }

    onVariantChange(event) {
      // Handle variant changes - update prices and selling plan availability
      const variant = event.detail?.variant;
      if (!variant) return;

      // Re-fetch selling plans data for new variant if needed
      // This would require updating the JSON data
    }

    showDetailsModal() {
      const modal = document.querySelector(`#SubscriptionDetails-${this.closest('[data-product-id]')?.dataset.productId}`);
      if (modal) {
        modal.hidden = false;
        document.body.style.overflow = 'hidden';

        // Close handlers
        const overlay = modal.querySelector('.subscription-details-modal__overlay');
        const closeBtn = modal.querySelector('.subscription-details-modal__close');

        const closeModal = () => {
          modal.hidden = true;
          document.body.style.overflow = '';
        };

        overlay?.addEventListener('click', closeModal, { once: true });
        closeBtn?.addEventListener('click', closeModal, { once: true });

        // ESC key handler
        const escHandler = (e) => {
          if (e.key === 'Escape') {
            closeModal();
            document.removeEventListener('keydown', escHandler);
          }
        };
        document.addEventListener('keydown', escHandler);
      }
    }
  }

  customElements.define('subscription-selector', SubscriptionSelector);
}

/**
 * Cart Subscription Selector
 * Uses event delegation to work with dynamically rendered cart content
 */
(function() {
  // Cart subscription helper functions
  const CartSubscriptionHelper = {
    async updateCartItem(key, quantity) {
      const response = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!response.ok) throw new Error('Failed to update cart item');
      return response.json();
    },

    async addToCart(variantId, quantity, sellingPlanId) {
      const body = {
        id: parseInt(variantId),
        quantity: quantity
      };
      if (sellingPlanId && sellingPlanId !== '0' && sellingPlanId !== '') {
        body.selling_plan = parseInt(sellingPlanId);
      }
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error('Failed to add item to cart');
      return response.json();
    },

    refreshCart() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      const sectionsToUpdate = ['mini-cart', 'cart-notification-product', 'cart-icon-bubble'];
      fetch(`${window.routes.cart_url}?sections=${sectionsToUpdate.join(',')}`)
        .then(response => response.json())
        .then(data => {
          sectionsToUpdate.forEach(sectionId => {
            const sectionContent = data[sectionId];
            if (sectionContent) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionContent, 'text/html');
              
              if (sectionId === 'mini-cart') {
                const miniCart = document.querySelector('.mini-cart');
                const newMiniCart = doc.querySelector('.mini-cart');
                if (miniCart && newMiniCart) {
                  miniCart.innerHTML = newMiniCart.innerHTML;
                  miniCart.classList.toggle('is-empty', newMiniCart.classList.contains('is-empty'));
                }
              }
              
              if (sectionId === 'cart-icon-bubble') {
                const bubble = document.getElementById('cart-icon-bubble');
                const newBubble = doc.getElementById('cart-icon-bubble');
                if (bubble && newBubble) {
                  bubble.innerHTML = newBubble.innerHTML;
                }
              }
            }
          });
        })
        .catch(error => {
          console.error('Error refreshing cart:', error);
          window.location.reload();
        });
    },

    setLoading(container, isLoading) {
      container.classList.toggle('is-loading', isLoading);
    }
  };

  // Event delegation for cart subscription checkboxes
  document.addEventListener('change', async function(event) {
    const checkbox = event.target.closest('[data-subscribe-checkbox]');
    if (!checkbox) return;

    const container = checkbox.closest('.cart-subscription-selector');
    if (!container) return;

    const frequencyContainer = container.querySelector('[data-frequency-container]');
    const frequencySelect = container.querySelector('[data-frequency-select]');
    const isChecked = checkbox.checked;

    if (isChecked) {
      // Show frequency dropdown
      if (frequencyContainer) {
        frequencyContainer.hidden = false;
      }
      
      // Convert to subscription
      if (frequencySelect) {
        const sellingPlanId = frequencySelect.value;
        const itemKey = container.dataset.itemKey;
        const variantId = container.dataset.variantId;
        const quantity = parseInt(container.dataset.quantity) || 1;

        CartSubscriptionHelper.setLoading(container, true);

        try {
          await CartSubscriptionHelper.updateCartItem(itemKey, 0);
          await CartSubscriptionHelper.addToCart(variantId, quantity, sellingPlanId);
          CartSubscriptionHelper.refreshCart();
        } catch (error) {
          console.error('Error converting to subscription:', error);
          checkbox.checked = false;
          if (frequencyContainer) frequencyContainer.hidden = true;
          CartSubscriptionHelper.setLoading(container, false);
        }
      }
    } else {
      // Hide frequency dropdown
      if (frequencyContainer) {
        frequencyContainer.hidden = true;
      }
    }
  });

  // Event delegation for frequency select changes
  document.addEventListener('change', async function(event) {
    const select = event.target.closest('[data-frequency-select]');
    if (!select) return;

    const container = select.closest('.cart-subscription-selector');
    if (!container) return;

    const checkbox = container.querySelector('[data-subscribe-checkbox]');
    if (!checkbox || !checkbox.checked) return;

    const sellingPlanId = select.value;
    const itemKey = container.dataset.itemKey;
    const variantId = container.dataset.variantId;
    const quantity = parseInt(container.dataset.quantity) || 1;

    CartSubscriptionHelper.setLoading(container, true);

    try {
      await CartSubscriptionHelper.updateCartItem(itemKey, 0);
      await CartSubscriptionHelper.addToCart(variantId, quantity, sellingPlanId);
      CartSubscriptionHelper.refreshCart();
    } catch (error) {
      console.error('Error changing subscription frequency:', error);
      CartSubscriptionHelper.setLoading(container, false);
    }
  });
})();
